// @ts-check
/**
 * Create a gift/sample order — pick items, capture (or AI-extract, or reuse from the
 * Influencer CRM) the recipient's details, and create a zero-value order that deducts
 * real stock. See docs/superpowers/specs/2026-09-17-influencer-gift-orders-design.md.
 */
import { Plus, Sparkles, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import adminGiftOrderService from '../services/adminGiftOrderService';
import adminInventoryService from '../services/adminInventoryService';
import influencerService from '../services/influencerService';
import Modal from './ui/Modal';

const emptyItem = () => ({ variant: null, quantity: 1 });

// Declared value defaults to the real product price — admin-editable downward, since
// sending product out is a pure cost to the company either way, and a lower declared
// value also means a lower customs/insurance-driven shipping cost.
const priceOf = (variant) => Number(variant.price ?? 0) + Number(variant.price_adjustment ?? 0);

/** @param {{ influencer?: any, onClose: () => void, onCreated?: (order: any) => void }} props */
const GiftOrderModal = ({ influencer: influencerProp, onClose, onCreated }) => {
  const toast = useToast();
  // Opened from an influencer's profile -> fixed. Opened from Create order -> the admin
  // picks an existing influencer; gifts are never sent to someone not in the CRM.
  const [pickedInfluencer, setPickedInfluencer] = useState(null);
  const influencer = influencerProp || pickedInfluencer;
  const [influencerQuery, setInfluencerQuery] = useState('');
  const [influencerResults, setInfluencerResults] = useState([]);
  const [loadingInfluencers, setLoadingInfluencers] = useState(!influencerProp);
  const [orderType, setOrderType] = useState('gift');
  const [items, setItems] = useState([emptyItem()]);
  const [variantQuery, setVariantQuery] = useState('');
  const [variantResults, setVariantResults] = useState([]);
  const [searchingVariants, setSearchingVariants] = useState(false);
  const [aiText, setAiText] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [saveAddress, setSaveAddress] = useState(false);
  const [saving, setSaving] = useState(false);
  // Touched-since-open flag, reset after a successful submit.
  const [isDirty, setIsDirty] = useState(false);

  // Cancel/X dismiss the modal — confirm first if there's anything to lose.
  const handleCloseClick = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    onClose();
  };

  const [form, setFormRaw] = useState({
    recipient_first_name: influencer?.name?.split(' ')[0] || '',
    recipient_last_name: influencer?.name?.split(' ').slice(1).join(' ') || '',
    recipient_email: '',
    recipient_phone: influencer?.shipping_phone || '',
    shipping_address_line1: influencer?.shipping_address_line1 || '',
    shipping_address_line2: influencer?.shipping_address_line2 || '',
    shipping_city: influencer?.shipping_city || '',
    shipping_state: influencer?.shipping_state || '',
    shipping_postal_code: influencer?.shipping_postal_code || '',
    shipping_country: influencer?.shipping_country || '',
    admin_notes: influencer ? `Gift for @${influencer.ig_username}` : '',
  });
  const setForm = useCallback((updater) => {
    setIsDirty(true);
    setFormRaw(updater);
  }, []);

  useEffect(() => {
    if (influencerProp) return undefined;
    const timer = setTimeout(async () => {
      setLoadingInfluencers(true);
      try {
        const { items: found } = await influencerService.getInfluencers({ search: influencerQuery.trim(), limit: 8 });
        setInfluencerResults(found || []);
      } catch (err) {
        toast.error(err.message || 'Failed to load influencers');
      } finally {
        setLoadingInfluencers(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [influencerProp, influencerQuery, toast]);

  const pickInfluencer = (inf) => {
    setPickedInfluencer(inf);
    setIsDirty(true);
    setFormRaw((prev) => ({
      ...prev,
      recipient_first_name: inf.name?.split(' ')[0] || '',
      recipient_last_name: inf.name?.split(' ').slice(1).join(' ') || '',
      recipient_phone: inf.shipping_phone || prev.recipient_phone,
      shipping_address_line1: inf.shipping_address_line1 || '',
      shipping_address_line2: inf.shipping_address_line2 || '',
      shipping_city: inf.shipping_city || '',
      shipping_state: inf.shipping_state || '',
      shipping_postal_code: inf.shipping_postal_code || '',
      shipping_country: inf.shipping_country || '',
      admin_notes: `Gift for @${inf.ig_username}`,
    }));
  };

  const searchVariants = async (query) => {
    setVariantQuery(query);
    if (!query.trim()) {
      setVariantResults([]);
      return;
    }
    setSearchingVariants(true);
    try {
      const { items: results } = await adminInventoryService.getAllVariants({ search: query, limit: 10 });
      setVariantResults(results || []);
    } catch (err) {
      toast.error('Failed to search products');
    } finally {
      setSearchingVariants(false);
    }
  };

  const addItem = (variant) => {
    const existing = items.filter((i) => i.variant);
    const matchIndex = existing.findIndex((i) => i.variant.variant_id === variant.variant_id);
    if (matchIndex >= 0) {
      const next = [...existing];
      next[matchIndex] = { ...next[matchIndex], quantity: next[matchIndex].quantity + 1 };
      setItems(next);
    } else {
      setItems([...existing, { variant, quantity: 1, unitPrice: priceOf(variant) }]);
    }
    setIsDirty(true);
    setVariantQuery('');
    setVariantResults([]);
  };

  const updateQuantity = (index, quantity) => {
    const next = [...items];
    next[index] = { ...next[index], quantity: Math.max(1, Number(quantity) || 1) };
    setItems(next);
    setIsDirty(true);
  };

  const updatePrice = (index, unitPrice) => {
    const next = [...items];
    next[index] = { ...next[index], unitPrice: Math.max(0, Number(unitPrice) || 0) };
    setItems(next);
    setIsDirty(true);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  const handleExtract = async () => {
    if (!aiText.trim()) return;
    setExtracting(true);
    try {
      const extracted = await adminGiftOrderService.extractRecipient(aiText);
      setForm((prev) => ({
        ...prev,
        recipient_first_name: extracted.name?.split(' ')[0] || prev.recipient_first_name,
        recipient_last_name: extracted.name?.split(' ').slice(1).join(' ') || prev.recipient_last_name,
        recipient_email: extracted.email || prev.recipient_email,
        recipient_phone: extracted.phone || prev.recipient_phone,
        shipping_address_line1: extracted.address_line1 || prev.shipping_address_line1,
        shipping_address_line2: extracted.address_line2 || prev.shipping_address_line2,
        shipping_city: extracted.city || prev.shipping_city,
        shipping_state: extracted.state || prev.shipping_state,
        shipping_postal_code: extracted.postal_code || prev.shipping_postal_code,
        shipping_country: extracted.country || prev.shipping_country,
      }));
      toast.success('Fields extracted — review before saving');
    } catch (err) {
      toast.error(err.message || 'AI extraction failed — fill the form manually');
    } finally {
      setExtracting(false);
    }
  };

  const handleSubmit = async () => {
    const validItems = items.filter((i) => i.variant);
    if (!influencer) {
      toast.error('Select an influencer first');
      return;
    }
    if (validItems.length === 0) {
      toast.error('Add at least one item');
      return;
    }
    if (!form.recipient_first_name.trim() || !form.shipping_address_line1.trim()) {
      toast.error('Recipient name and address are required');
      return;
    }
    if (
      !form.shipping_city.trim() ||
      !form.shipping_state.trim() ||
      !form.shipping_postal_code.trim() ||
      !form.shipping_country.trim()
    ) {
      toast.error('City, state, postal code, and country are required');
      return;
    }
    if (!form.recipient_phone.trim()) {
      // Both ShipGlobal and Shiprocket reject a booking with no phone (400, not a crash) —
      // catching it here avoids the admin discovering that only after creating the order.
      toast.error('A phone number is required to book a shipment with either carrier');
      return;
    }

    setSaving(true);
    try {
      const order = await adminGiftOrderService.createGiftOrder({
        order_type: orderType,
        influencer_id: influencer.id,
        ...form,
        items: validItems.map((i) => ({
          product_id: i.variant.product_id,
          variant_id: i.variant.variant_id,
          size: i.variant.size,
          quantity: i.quantity,
          unit_price: i.unitPrice,
        })),
        save_address_to_influencer: saveAddress && !!influencer,
      });
      toast.success(`Gift order ${order.order_number} created`);
      setIsDirty(false);
      onCreated?.(order);
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to create gift order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal maxWidth="2xl" onClose={handleCloseClick} hasUnsavedChanges={isDirty}>
      <div className="flex justify-between items-center px-6 py-4 border-b">
        <h2 className="text-lg font-semibold">Send Gift{influencer ? ` — ${influencer.name}` : ''}</h2>
        <select
          value={orderType}
          onChange={(e) => setOrderType(e.target.value)}
          className="text-sm border border-input rounded-lg px-2 py-1"
        >
          <option value="gift">Gift</option>
          <option value="sample">Sample</option>
        </select>
      </div>

      <div className="px-6 py-4 space-y-4 max-h-[65vh] overflow-y-auto">
        {!influencerProp && (
          <div>
            <label className="block text-sm font-medium mb-1">Influencer *</label>
            {pickedInfluencer ? (
              <div className="flex items-center justify-between border border-border rounded-lg px-3 py-2 text-sm">
                <span>
                  {pickedInfluencer.name} <span className="text-muted-foreground">@{pickedInfluencer.ig_username}</span>
                </span>
                <button onClick={() => setPickedInfluencer(null)} className="text-xs text-muted-foreground hover:underline">
                  Change
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={influencerQuery}
                  onChange={(e) => setInfluencerQuery(e.target.value)}
                  placeholder="Search influencers by name or @handle..."
                  className="w-full px-3 py-2 text-sm border border-input rounded-lg"
                />
                {loadingInfluencers && <p className="text-xs text-muted-foreground/70 mt-1">Searching...</p>}
                {influencerResults.length > 0 && (
                  <div className="border border-border rounded-lg mt-1 max-h-40 overflow-y-auto">
                    {influencerResults.map((inf) => (
                      <button
                        key={inf.id}
                        onClick={() => pickInfluencer(inf)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 flex justify-between"
                      >
                        <span>{inf.name}</span>
                        <span className="text-muted-foreground/70">@{inf.ig_username}</span>
                      </button>
                    ))}
                  </div>
                )}
                {!loadingInfluencers && influencerResults.length === 0 && (
                  <p className="text-sm text-muted-foreground mt-2">
                    {influencerQuery.trim() ? 'No influencer matches that search.' : 'No influencers yet.'} Create the
                    influencer first, then come back to create the gift order.{' '}
                    <Link to="/influencers" onClick={onClose} className="underline font-medium">
                      Go to Influencers
                    </Link>
                  </p>
                )}
              </>
            )}
          </div>
        )}

        <div className="border border-border rounded-lg p-3 bg-muted/50">
          <label className="block text-sm font-medium mb-1 flex items-center gap-1">
            <Sparkles size={14} /> Extract from pasted text (DM, email, message)
          </label>
          <textarea
            value={aiText}
            onChange={(e) => setAiText(e.target.value)}
            rows={3}
            placeholder="Paste the influencer's message with their address here..."
            className="w-full px-3 py-2 text-sm border border-input rounded-lg"
          />
          <button
            onClick={handleExtract}
            disabled={extracting || !aiText.trim()}
            className="mt-2 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {extracting ? 'Extracting...' : 'Extract with AI'}
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Items</label>
          {items
            .filter((i) => i.variant)
            .map((item, index) => (
              <div key={`${item.variant.variant_id}-${index}`} className="flex items-center gap-2 mb-2">
                <span className="flex-1 text-sm">
                  {item.variant.product_name} — Size {item.variant.size}
                  {item.variant.color ? ` (${item.variant.color})` : ''}
                </span>
                <label className="flex items-center gap-1 text-xs text-muted-foreground">
                  Qty
                  <input
                    type="number"
                    min={1}
                    value={item.quantity}
                    onChange={(e) => updateQuantity(index, e.target.value)}
                    className="w-16 px-2 py-1 text-sm border border-input rounded-lg"
                  />
                </label>
                <label className="flex items-center gap-1 text-xs text-muted-foreground">
                  {'₹'}
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={item.unitPrice}
                    onChange={(e) => updatePrice(index, e.target.value)}
                    title="Declared value — lower this since it's a cost to the company either way, and a lower value means lower customs/insurance-driven shipping cost"
                    className="w-24 px-2 py-1 text-sm border border-input rounded-lg"
                  />
                </label>
                <button
                  onClick={() => removeItem(index)}
                  className="text-muted-foreground/70 hover:text-red-600 dark:hover:text-red-400"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          <input
            type="text"
            value={variantQuery}
            onChange={(e) => searchVariants(e.target.value)}
            placeholder="Search products by name or SKU..."
            className="w-full px-3 py-2 text-sm border border-input rounded-lg"
          />
          {searchingVariants && <p className="text-xs text-muted-foreground/70 mt-1">Searching...</p>}
          {variantResults.length > 0 && (
            <div className="border border-border rounded-lg mt-1 max-h-40 overflow-y-auto">
              {variantResults.map((v) => (
                <button
                  key={v.variant_id}
                  onClick={() => addItem(v)}
                  disabled={v.stock_quantity <= 0}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 disabled:opacity-40 flex justify-between"
                >
                  <span>
                    {v.product_name} — Size {v.size}
                    {v.color ? ` (${v.color})` : ''}
                  </span>
                  <span className="text-muted-foreground/70">{v.stock_quantity} in stock</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            placeholder="First name *"
            value={form.recipient_first_name}
            onChange={(e) => setForm({ ...form, recipient_first_name: e.target.value })}
            className="px-3 py-2 text-sm border border-input rounded-lg"
          />
          <input
            placeholder="Last name"
            value={form.recipient_last_name}
            onChange={(e) => setForm({ ...form, recipient_last_name: e.target.value })}
            className="px-3 py-2 text-sm border border-input rounded-lg"
          />
          <input
            placeholder="Email"
            value={form.recipient_email}
            onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
            className="px-3 py-2 text-sm border border-input rounded-lg"
          />
          <input
            placeholder="Phone *"
            value={form.recipient_phone}
            onChange={(e) => setForm({ ...form, recipient_phone: e.target.value })}
            className="px-3 py-2 text-sm border border-input rounded-lg"
          />
          <input
            placeholder="Address line 1 *"
            value={form.shipping_address_line1}
            onChange={(e) => setForm({ ...form, shipping_address_line1: e.target.value })}
            className="col-span-2 px-3 py-2 text-sm border border-input rounded-lg"
          />
          <input
            placeholder="Address line 2"
            value={form.shipping_address_line2}
            onChange={(e) => setForm({ ...form, shipping_address_line2: e.target.value })}
            className="col-span-2 px-3 py-2 text-sm border border-input rounded-lg"
          />
          <input
            placeholder="City *"
            value={form.shipping_city}
            onChange={(e) => setForm({ ...form, shipping_city: e.target.value })}
            className="px-3 py-2 text-sm border border-input rounded-lg"
          />
          <input
            placeholder="State *"
            value={form.shipping_state}
            onChange={(e) => setForm({ ...form, shipping_state: e.target.value })}
            className="px-3 py-2 text-sm border border-input rounded-lg"
          />
          <input
            placeholder="Postal code *"
            value={form.shipping_postal_code}
            onChange={(e) => setForm({ ...form, shipping_postal_code: e.target.value })}
            className="px-3 py-2 text-sm border border-input rounded-lg"
          />
          <input
            placeholder="Country *"
            value={form.shipping_country}
            onChange={(e) => setForm({ ...form, shipping_country: e.target.value })}
            className="px-3 py-2 text-sm border border-input rounded-lg"
          />
        </div>

        <textarea
          placeholder="Internal notes"
          value={form.admin_notes}
          onChange={(e) => setForm({ ...form, admin_notes: e.target.value })}
          rows={2}
          className="w-full px-3 py-2 text-sm border border-input rounded-lg"
        />

        {influencer && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={saveAddress} onChange={(e) => setSaveAddress(e.target.checked)} />
            Save this address to {influencer.name}'s profile for next time
          </label>
        )}
      </div>

      <div className="flex gap-2 px-6 py-4 border-t">
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:bg-muted-foreground/50 flex items-center gap-1"
        >
          <Plus size={14} /> {saving ? 'Creating...' : 'Create Gift Order'}
        </button>
        <button
          onClick={handleCloseClick}
          disabled={saving}
          className="px-4 py-2 text-sm border border-input rounded-lg hover:bg-accent"
        >
          Cancel
        </button>
      </div>
    </Modal>
  );
};

export default GiftOrderModal;
